import {
  //@ts-ignore
  createEffect,
  createRoot,
  createSignal,
  onMount,
  onCleanup,
} from "solid-js";
import { View, hexColor, Text, Show } from "@lightningtv/solid";
import { Column } from "@lightningtv/solid-ui";
import { LazyUp } from "@lightningtv/solid/primitives";
import { useLocation, useNavigate, useParams } from "@solidjs/router";
import moviesProvider from "../../api/providers/movies";
import { createInfiniteScroll } from "../../components/pagination";
import moviesCategory from "../../api/providers/moviesCategory";
import LoadingIndicator from "../loading/loading";
import { focusStore, setFocusStore } from "../../../state";
import { fontFamily, isRTL, t } from "../../translations/configuration";
import ConfirmPin from "../../components/ConfirmPin";
import moviesLastAdded from "../../api/providers/moviesLastAdded";
import moviesContinueWatching from "../../api/providers/moviesContinueWatching";
import moviesFavorites from "../../api/providers/moviesFavorites";
import Header from "../../components/Header";
import { IconButton } from "../../components/playlistComponents/TopRightHeaders";


const STAR_INDICES = [0, 1, 2, 3, 4];

const Movies = () => {
  const params = useParams();
  const location = useLocation();
  const [lastElement, setLastElement] = createSignal<any>(null);
  const navigate = useNavigate();
  const [number, setNumber] = createSignal<any>(null);
  const [isNumberChanging, setIsNumberChanging] = createSignal(false);
  const [shouldWait, setShouldWait] = createSignal(false);
  const [showLoading, setShowLoading] = createSignal(true);
  const [showMessage, setShowMessage] = createSignal(false);
  const [toggledCategoryIDs, setToggledCategoryIDs] = createSignal(
    JSON.parse(localStorage.getItem("movieCategoryIDs") || "[]")
  );
  const [showPinDialog, setShowPinDialog] = createSignal(false);
  const [currentAction, setCurrentAction] = createSignal(null);
  const [currentCategoryData, setCurrentCategoryData] = createSignal(null);
  const [pendingNavigation, setPendingNavigation] = createSignal(null);
  const [lastFocusedLazyUp, setLastFocusedLazyUp] = createSignal('first');
  const [heldCategory, setHeldCategory] = createSignal<{ id: number; callback: () => void } | null>(null);
  const [focusedCategoryName, setFocusedCategoryName] = createSignal<string>("");

  const savedPin = localStorage.getItem('userPin');

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let focusTimeout: any;
  let myButton: any;
  let viewRef: any;
  let firstRef: any;
  let search: any;
  let settings: any;
  let cancelExit;
  let okExit;
  let headerRef: any;

  const providerCategory = createInfiniteScroll(moviesCategory(params.filter || "all"));

  // provider is recreated (with proper reactive-root lifecycle) when number() changes.
  // Using createRoot ensures the child signals/resource are properly owned and disposed.
  const [provider, setProvider] = createSignal<ReturnType<typeof createInfiniteScroll> | null>(null, { equals: false });
  let disposeCurrentProvider: (() => void) | null = null;

  createEffect(() => {
    const selectedCategory = number();
    if (disposeCurrentProvider) { disposeCurrentProvider(); disposeCurrentProvider = null; }
    createRoot((dispose) => {
      disposeCurrentProvider = dispose;
      let scroll: ReturnType<typeof createInfiniteScroll>;
      if (selectedCategory === "continue_watching") {
        scroll = createInfiniteScroll(moviesContinueWatching(params.filter || "all"));
      } else if (selectedCategory === "favorites") {
        scroll = createInfiniteScroll(moviesFavorites(params.filter || "all"));
      } else if (selectedCategory === "last_added") {
        scroll = createInfiniteScroll(moviesLastAdded(params.filter || "all"));
      } else {
        scroll = createInfiniteScroll(moviesProvider(params.filter || "all", selectedCategory));
      }
      setProvider(scroll);
    });
  });

  onCleanup(() => { if (disposeCurrentProvider) disposeCurrentProvider(); });

  const hasContinueWatchingCategory = () => {
    const categories = providerCategory.pages();
    return categories.length > 0 && categories[0]?.categoryID === "continue_watching";
  };

  const getAdjustedSelectedIndex = () => {
    const storedIndex = focusStore.moviesIndex;
    if (storedIndex === null) return 0;
    const currentlyHasCW = hasContinueWatchingCategory();
    const adjustedFlag = localStorage.getItem("movies_cw_adjusted");
    if (currentlyHasCW && adjustedFlag !== "true" && storedIndex >= 0) {
      localStorage.setItem("movies_cw_adjusted", "true");
      return storedIndex + 1;
    }
    return storedIndex;
  };

  createEffect(() => {
    const categories = providerCategory.pages();
    if (!categories || categories.length === 0) return;

    const savedPin = localStorage.getItem('userPin');
    if (!savedPin) {
      localStorage.removeItem("movieCategoryIDs");
      return;
    }

    const adultCategoryIDs = categories
      .filter(cat => ["xxx", "porn", "adult"].some(keyword => cat.category_name.toLowerCase().includes(keyword)))
      .map(cat => cat.categoryID);

    const existingIDs = JSON.parse(localStorage.getItem("movieCategoryIDs") || "[]");
    const mergedIDs = [...new Set([...existingIDs, ...adultCategoryIDs])];
    localStorage.setItem("movieCategoryIDs", JSON.stringify(mergedIDs));

    const currentlyHasCW = hasContinueWatchingCategory();
    const adjustedFlag = localStorage.getItem("movies_cw_adjusted");
    if (!currentlyHasCW && adjustedFlag === "true") {
      localStorage.removeItem("movies_cw_adjusted");
    }
  });

  const handleNumberChange = (newNumber: any) => {
    if (number() === newNumber) return;
    if (timeoutId) clearTimeout(timeoutId);
    setIsNumberChanging(true);
    timeoutId = setTimeout(() => {
      setNumber(newNumber);
      setIsNumberChanging(false);
      timeoutId = null;
    }, 500);
  };

  const handlePinConfirm = (pin) => {
    const action = currentAction();
    const categoryData = currentCategoryData();
    const navigation = pendingNavigation();
    if (!categoryData) return;
    const savedPin = localStorage.getItem("userPin");
    if (pin === savedPin) {
      switch (action) {
        case 'lock':
          lockCategory(categoryData.id);
          break;
        case 'unlock':
          if (isAdultCategory(categoryData.id, categoryData.name)) break;
          unlockCategory(categoryData.id);
          break;
        case 'access':
          if (navigation) {
            navigate(navigation.url);
            setFocusStore("moviesDetail", navigation.index);
          }
          break;
      }
    }
    setShowPinDialog(false);
    setCurrentAction(null);
    setCurrentCategoryData(null);
    setPendingNavigation(null);
  };

  const isAdultCategory = (categoryID: string, categoryName: string) => {
    const hasAdultKeyword = ["xxx", "porn", "adult"].some(keyword => categoryName.toLowerCase().includes(keyword));
    const categories = providerCategory.pages();
    const adultCategoryIDs = categories
      .filter(cat => ["xxx", "porn", "adult"].some(keyword => cat.category_name.toLowerCase().includes(keyword)))
      .map(cat => cat.categoryID);
    return hasAdultKeyword || adultCategoryIDs.includes(categoryID);
  };

  const lockCategory = (categoryID) => {
    const savedIDs = [...toggledCategoryIDs()];
    if (!savedIDs.includes(categoryID)) {
      savedIDs.push(categoryID);
      localStorage.setItem("movieCategoryIDs", JSON.stringify(savedIDs));
      setToggledCategoryIDs(savedIDs);
    }
  };

  const unlockCategory = (categoryID) => {
    const savedIDs = [...toggledCategoryIDs()];
    const indexInArray = savedIDs.indexOf(categoryID);
    if (indexInArray !== -1) {
      savedIDs.splice(indexInArray, 1);
      localStorage.setItem("movieCategoryIDs", JSON.stringify(savedIDs));
      setToggledCategoryIDs(savedIDs);
    }
  };

  const handleMovieAccess = (movieData, movieIndex, categoryId) => {
    const isLocked = toggledCategoryIDs().includes(categoryId);
    if (isLocked) {
      setCurrentCategoryData({ id: categoryId });
      setCurrentAction('access');
      setPendingNavigation({
        url: `/entity/movie/${movieData.stream_id}/${movieData.category_id}`,
        index: movieIndex
      });
      setShowPinDialog(true);
    } else {
      navigate(`/entity/movie/${movieData.stream_id}/${movieData.category_id}`);
      setFocusStore("moviesDetail", movieIndex);
    }
  };

  const [isDialogOpenExit, setIsDialogOpenExit] = createSignal<boolean>(false);

  const enterHoldFunc = (categoryID, toggleCategoryCallback) => {
    if (localStorage.getItem('userPin') === null) {
      setTimeout(() => { cancelExit.setFocus() }, 600);
      setIsDialogOpenExit(true);
    } else {
      toggleCategoryCallback();
    }
  };

  let holdTimer = null;
  let enterHeld = false;

  const handleKeyDown = (e) => {
    const held = heldCategory();
    if (!held) return;
    if ((e.key === "Enter" || e.keyCode === 13) && !holdTimer) {
      holdTimer = setTimeout(() => {
        enterHeld = true;
        enterHoldFunc(held.id, held.callback);
      }, 600);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
      enterHeld = false;
    }
  };

  onMount(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
  });

  onCleanup(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
  });

  function hasAnyContinueWatchingMovies() {
    try {
      const data = localStorage.getItem("vod_continue_watching");
      if (!data) return false;
      const list = JSON.parse(data);
      return Array.isArray(list) && list.length > 0;
    } catch { return false; }
  }

  // ── THUMBNAIL (category item in left panel) ───────────────────────────
  function Thumbnail(props: any) {
    const newData = props;
    const currentCategoryID = props.categoryID;
    const isToggled = () => toggledCategoryIDs().includes(currentCategoryID);
    const isLastAddedCategory = currentCategoryID === "last_added";
    const isContinueWatchingCategory = currentCategoryID === "continue_watching";

    const toggleCategory = () => {
      if (isContinueWatchingCategory || isLastAddedCategory) return;
      const isCurrentlyLocked = isToggled();
      setCurrentCategoryData({ id: currentCategoryID, name: newData.category_name });
      setCurrentAction(isCurrentlyLocked ? 'unlock' : 'lock');
      setShowPinDialog(true);
    };

    return (
      <View
        ref={myButton}
        style={{
          width: 480,
          height: 95,
          borderRadius: 12,
          color: newData.index === lastElement() ? hexColor("#15171b") : hexColor("#15171b"),
          border: { width: 0, color: hexColor("#2563eb") },
          focus: { border: { width: 2, color: hexColor("#2563eb") } },
          transition: { color: { duration: 200 } },
          justifyContent: "center",
          alignItems: "center",
        }}
        onFocus={() => {
          handleNumberChange(newData.categoryID);
          setFocusStore("moviesIndex", newData.index);
          setFocusStore("moviesID", newData.categoryID);
          setFocusedCategoryName(newData.category_name);

          if (!isContinueWatchingCategory && !isLastAddedCategory) {
            setHeldCategory({
              id: newData.categoryID,
              callback: () => {
                const isCurrentlyLocked = isToggled();
                setCurrentCategoryData({ id: newData.categoryID, name: newData.category_name });
                setCurrentAction(isCurrentlyLocked ? "unlock" : "lock");
                setShowPinDialog(true);
              },
            });
          } else {
            setHeldCategory(null);
          }
        }}
        onUp={() => { if (shouldWait()) return true; else setFocusStore("moviesDetail", 0); }}
        onDown={() => { if (shouldWait()) return true; else setFocusStore("moviesDetail", 0); }}
        onRight={() => { if (shouldWait()) return true; }}
        onLeft={() => { if (shouldWait()) return true; }}
      >
        {/* Left/right accent bar — mirrors in RTL */}
        <View
          x={isRTL() ? 476 : 0} y={10}
          width={4} height={60}
          borderRadius={4}
          style={{
            color: hexColor("#00000000"),
            focus: { color: hexColor("#2563eb") },
          }}
        />
        {/* Category name */}
        <Text
          x={24} y={37}
          width={432}
          contain="width"
          textAlign={isRTL() ? "right" : "left"}
          style={{
            fontSize: 26,
            color: hexColor("#e2e8f0"),
            fontFamily: fontFamily(),
          }}
        >
          {newData.category_name}
        </Text>

        {/* Lock / unlock icon — mirrors in RTL */}
        <Show when={savedPin && !isContinueWatchingCategory && !isLastAddedCategory}>
          <View
            src={isToggled() ? "https://i.ibb.co/jPvhs2HJ/lock.png" : "https://i.ibb.co/N2Ym8McH/unlock.png"}
            width={24} height={24}
            x={isRTL() ? 36 : 420} y={37}
            style={isToggled() && isAdultCategory(currentCategoryID, newData.category_name) ? { opacity: 0.6 } : {}}
          />
        </Show>
      </View>
    );
  }

  // ── ITEM MOVIE (right panel card) ──────────────────────────────────────
  function ItemMovie(props: any) {
    const newData = props;
    const rating = newData?.data?.rating_5based ?? 0;
    const hasContinueWatching = newData?.data?.continue_watching_time > 0;

    const continueWatchingProgress = () => {
      if (!hasContinueWatching) return 0;
      const time = newData.data.continue_watching_time;
      const duration = newData.data.continue_watching_duration;
      if (!duration || duration === 0) return 0;
      return Math.min(Math.max(time / duration, 0), 1);
    };

    const roundedRating = () => Math.round(rating * 2) / 2;

    const handleEnter = () => {
      const currentCategoryId = number();
      handleMovieAccess(newData.data, newData.index, currentCategoryId);
    };

    // Format the added date if available
    const addedDate = newData?.data?.added
      ? (() => {
        try {
          const d = new Date(parseInt(newData.data.added) * 1000);
          return `Added ${d.getDate()} ${d.toLocaleString('en', { month: 'short' })} ${d.getFullYear()}`;
        } catch { return null; }
      })()
      : null;

    return (
      <View
        x={20}
        ref={myButton}
        style={{
          borderRadius: 12,
          color: hexColor("#15171b"),
          border: { width: 0, color: hexColor("#2563eb") },
          focus: { border: { width: 2, color: hexColor("#2563eb") } },
          transition: { color: { duration: 200 } },
          height: 200,
          width: 1200,
        }}
        onFocus={() => { }}
        onEnter={handleEnter}
      >
        {/* Accent bar — mirrors in RTL */}
        <View
          x={isRTL() ? 1191 : 5} y={20}
          width={4} height={160}
          borderRadius={4}
          style={{
            color: hexColor("#00000000"),
            focus: { color: hexColor("#2563eb") },
          }}
        />

        {/* ── Poster thumbnail — right side in RTL ── */}
        <View
          src={newData.src}
          x={isRTL() ? 1052 : 18} y={15}
          style={{ width: 130, height: 170, borderRadius: 8 }}
        />

        {/* ── Content area ── */}
        <View x={isRTL() ? 120 : 168} y={20}>

          {/* Title */}
          <Text
            x={0} y={0}
            style={{
              fontSize: 28,
              color: hexColor("#f0f6fc"),
              fontWeight: isRTL() ? "normal" : "bold",
              fontFamily: fontFamily(),
            }}
          >
            {newData.data.name}
          </Text>

          {/* Badges row: "Last Added" badge + date */}
          <View x={0} y={46}>
            <Show when={focusedCategoryName()}>
              <View
                x={0} y={0}
                width={Math.min(focusedCategoryName().length * 10 + 20, 380)} height={32}
                borderRadius={6}
                color={hexColor("#1d4ed8")}
              >
                <Text x={10} y={7} style={{ fontSize: 18, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold" }}>
                  {focusedCategoryName()}
                </Text>
              </View>
            </Show>

            <Show when={addedDate}>
              <Text
                x={Math.min(focusedCategoryName().length * 12 + 10, 380)} y={6}
                style={{ fontSize: 20, color: hexColor("#8b949e") }}
              >
                {addedDate}
              </Text>
            </Show>
          </View>

          {/* Stars + IMDb — y=100 gives breathing room below badges */}
          <View x={0} y={100}>
            {STAR_INDICES.map((index) => {
              const fullStars = Math.floor(rating);
              const hasHalfStar = rating % 1 >= 0.5;
              let starSrc = "https://i.ibb.co/1KBsPWh/star-2.png";
              if (index < fullStars) starSrc = "https://i.ibb.co/4gK6MnK/star-1.png";
              else if (index === fullStars && hasHalfStar) starSrc = "https://i.ibb.co/9V7LVwc/rating.png";
              return <View key={index} x={index * 34} y={0} width={26} height={26} src={starSrc} />;
            })}
            <Text
              x={180} y={3}
              style={{ fontSize: 22, color: hexColor("#8b949e") }}
            >
              {roundedRating()}  IMDb
            </Text>
          </View>

          {/* Continue watching progress bar */}
          <Show when={hasContinueWatching}>
            <View x={0} y={145} width={400} height={6} borderRadius={3} color={hexColor("#2d3748")}>
              <View
                x={0} y={0}
                width={400 * continueWatchingProgress()}
                height={6}
                borderRadius={3}
                color={hexColor("#f5c518")}
              />
            </View>
          </Show>
        </View>

        {/* ── MP4 badge — top-right (LTR) / top-left (RTL) ── */}
        <Show when={newData?.data?.container_extension}>
          <View
            x={isRTL() ? 30 : 1110} y={40}
            width={60} height={32}
            borderRadius={6}
            color={hexColor("#1d4ed8")}
          >
            <Text x={10} y={7} style={{ fontSize: 17, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold" }}>
              {newData.data.container_extension?.toUpperCase()}
            </Text>
          </View>
        </Show>
      </View>
    );
  }

  createEffect(() => {
    localStorage.setItem("@emptyCategory", providerCategory.pages()?.length > 0 ? "false" : "true");
    const timeout = setTimeout(() => {
      setShowMessage(providerCategory.pages().length > 0 ? false : true);
      setShowLoading(false);
    }, 15000);
    onCleanup(() => clearTimeout(timeout));
  });

  createEffect(() => {
    const enterID = focusStore.moviesID;
    if (enterID !== null) handleNumberChange(enterID);
  });

  onMount(() => {
    if (focusStore.moviesDetail === null) return;
    setShouldWait(true);
    if (providerCategory && location.pathname.startsWith("/movies")) {
      const timeout = setTimeout(() => {
        viewRef?.setFocus();
        setShouldWait(false);
      }, 1500);
      onCleanup(() => clearTimeout(timeout));
    } else {
      setShouldWait(false);
    }
  });

  const handleEscape = () => {
    if (!shouldWait()) {
      setFocusStore("moviesIndex", null);
      setFocusStore("moviesDetail", null);
      setFocusStore("moviesID", null);
      clearTimeout(focusTimeout);
      focusTimeout = null;
      if (location.pathname.startsWith("/search")) {
        navigate("/movies");
      } else {
        navigate("/home");
      }
    }
  };

  return (
    <Show when={showPinDialog()} fallback={
      <View color={hexColor("#0d0f14")} onEscape={handleEscape}>

        <Header screen={t("bigMovies")} ref={(el) => headerRef = el}
          onRight={() => isRTL() ? null : search?.setFocus()}
          onLeft={() => isRTL() ? settings?.setFocus() : null}
          onDown={() => {
            if (location.pathname.startsWith("/movies")) {
              if (lastFocusedLazyUp() === 'second') viewRef.setFocus();
              else firstRef.setFocus();
            }
          }}
        />

        {/* ── No-PIN dialog ── */}
        <Show when={isDialogOpenExit()}>
          <View x={500} y={350} width={800} height={300} color={hexColor("#ffffff")} borderRadius={20}>
            <Text x={50} y={30} style={{ fontSize: 24, color: hexColor("#000000"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("pinNotSet")}
            </Text>
            <Text x={50} y={100} style={{ fontSize: 24, color: hexColor("#000000"), fontFamily: fontFamily() }}>
              {t("setPinNow")}
            </Text>
            <View
              ref={el => { cancelExit = el; }}
              x={430} y={220}
              style={{ borderRadius: 15, color: hexColor("#C1C3C1"), focus: { color: hexColor("#128DD3") }, transition: { scale: { duration: 250, easing: "ease-in-out" } }, height: 45, width: 150 }}
              onEnter={() => { firstRef.setFocus(); setIsDialogOpenExit(false); }}
              textColor={hexColor("#000000")}
              onRight={() => okExit.setFocus()}
            >
              <Text x={40} y={15} style={{ fontSize: 18, color: hexColor("#000000"), fontFamily: fontFamily() }}>{t("cancelBig")}</Text>
            </View>
            <View
              ref={el => { okExit = el; }}
              x={600} y={220}
              style={{ borderRadius: 15, color: hexColor("#C1C3C1"), focus: { color: hexColor("#128DD3") }, transition: { scale: { duration: 250, easing: "ease-in-out" } }, height: 45, width: 150 }}
              onLeft={() => cancelExit.setFocus()}
              onEnter={() => navigate("/userinfo?fromPIN=true")}
              textColor={hexColor("#000000")}
            >
              <Text x={60} y={15} style={{ fontSize: 18, color: hexColor("#000000"), fontFamily: fontFamily() }}>{t("ok")}</Text>
            </View>
          </View>
        </Show>

        {/* ── Top-right buttons ── */}
        <View x={isRTL() ? 50 : 1700} y={50}>
          <IconButton
            onRef={(el) => { search = el; }}
            x={0} y={0}
            src="https://i.ibb.co/nNhBcw8M/search.png"
            onRight={() => settings.setFocus()}
            onLeft={() => isRTL() ? null : headerRef?.setFocus()}
            onDown={() => {
              if (location.pathname.startsWith("/movies")) {
                if (lastFocusedLazyUp() === 'second') viewRef.setFocus();
                else firstRef.setFocus();
              }
            }}
            onEnter={() => { navigate("/smartSearch") }}
          // autofocus={data().length === 0 && !loading() && languageExists() && acceptTerms() && pinExists() && !showUpdateModal() && !showKillSwitchBlockModal() && !isDialogOpen() && !isDialogOpenExit()}
          />

          <IconButton
            onRef={(el) => { settings = el; }}
            x={100} y={0}
            src="https://i.ibb.co/Jjp8bmn4/setting.png"
            onLeft={() => search.setFocus()}
            onRight={() => isRTL() ? headerRef?.setFocus() : null}
            onDown={() => {
              if (location.pathname.startsWith("/movies")) {
                if (lastFocusedLazyUp() === 'second') viewRef.setFocus();
                else firstRef.setFocus();
              }
            }}
            onEnter={() => navigate("/userInfo")}
          />
        </View>

        {/* ── Main content ── */}
        <View y={150}>
          <Show
            when={providerCategory.pages() && providerCategory.pages()?.length > 0}
            fallback={
              <View x={750} y={500}>
                <Text fontSize={30} color={hexColor("#7d7e82")} style={{ fontFamily: fontFamily() }}>
                  {t("noMovies")}
                </Text>
              </View>
            }
          >
            <View clipping y={30}>

              {/* ── LEFT: Categories column ── */}
              {/* @ts-ignore */}
              <LazyUp
                onRight={() => {
                  if (!isRTL()) {
                    if (provider()?.pages() && provider()?.pages()?.length > 0 && !isNumberChanging()) {
                      viewRef.setFocus();
                      setShowLoading(false);
                    } else {
                      setShowLoading(true);
                    }
                  }
                }}
                onLeft={() => {
                  if (isRTL()) {
                    if (provider()?.pages() && provider()?.pages()?.length > 0 && !isNumberChanging()) {
                      viewRef.setFocus();
                      setShowLoading(false);
                    } else {
                      setShowLoading(true);
                    }
                  }
                }}
                onBlur={() => setHeldCategory(null)}
                onFocus={() => setLastFocusedLazyUp('first')}
                onUp={() => search.setFocus()}
                x={isRTL() ? 1350 : 60}
                onSelectedChanged={setLastElement}
                y={10}
                gap={10}
                autofocus={true}
                ref={firstRef}
                component={Column}
                scroll="always"
                upCount={15}
                selected={getAdjustedSelectedIndex()}
                each={providerCategory.pages()}
              >
                {(item: any, i: number) => <Thumbnail {...item} index={i} />}
              </LazyUp>

              {/* ── RIGHT: Movies column ── */}
              <View x={isRTL() ? 70 : 540} y={10} clipping>
                <Show
                  when={provider()?.pages() && provider()?.pages()?.length > 0 && !isNumberChanging()}
                  fallback={
                    <View x={400} y={300} width={100} height={100}>
                      <Show when={number() !== "favorites"}>
                        {showLoading() && <LoadingIndicator isCategoriesIndicator={true} />}
                        {showMessage() && (
                          <Text y={50} style={{ color: hexColor("#7d7e82"), fontSize: 22, width: 10, fontFamily: fontFamily() }}>
                            {t("categoryEmpty")}
                          </Text>
                        )}
                      </Show>
                      <Show when={number() === "favorites"}>
                        <Text y={50} style={{ color: hexColor("#7d7e82"), fontSize: 22, width: 10, fontFamily: fontFamily() }}>
                          {t("noFavMovies")}
                        </Text>
                      </Show>
                    </View>
                  }
                >
                  {/* @ts-ignore */}
                  <LazyUp
                    x={20}
                    onLeft={() => isRTL() ? null : firstRef.setFocus()}
                    onRight={() => isRTL() ? firstRef.setFocus() : null}
                    component={Column}
                    ref={viewRef}
                    onUp={() => search.setFocus()}
                    onFocus={() => setLastFocusedLazyUp('second')}
                    scroll="always"
                    upCount={6}
                    each={provider()?.pages()}
                    gap={12}
                    selected={
                      focusStore.moviesDetail === null
                        ? 0
                        : (() => {
                          const index = focusStore.moviesDetail;
                          if (index !== null) {
                            setTimeout(() => { }, 300);
                            return index;
                          }
                        })()
                    }
                  >
                    {(item: any, i: number) => <ItemMovie {...item} index={i} />}
                  </LazyUp>
                </Show>
              </View>

            </View>
          </Show>
        </View>

      </View>}>

      {/* ── PIN dialog overlay ── */}
      <View onEscape={() => {
        if (showPinDialog()) setShowPinDialog(false);
      }}>
        <ConfirmPin onPinSet={handlePinConfirm} />
      </View>
    </Show>
  );
};

export default Movies;